import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useMessaging } from '@/hooks/useMessaging';
import { useAuth } from '@/features/auth/AuthProvider';
import { useUsers } from '@/hooks/useUsers';
import { formatDistanceToNow } from 'date-fns';
import { getDateFnsLocale } from '@/lib/dateLocale';
import type { Conversation } from '@/types';

export const MessagesDropdown: React.FC = () => {
    const { currentUser } = useAuth();
    const { conversations } = useMessaging();
    const { users } = useUsers();
    const navigate = useNavigate();

    // Only show last 5 conversations
    const recentConversations = useMemo(() => {
        return conversations.slice(0, 5);
    }, [conversations]);

    const totalUnread = useMemo(() => {
        if (!currentUser) return 0;
        return conversations.reduce((sum, conv) => {
            return sum + (conv.unreadCounts?.[currentUser.uid] || 0);
        }, 0);
    }, [conversations, currentUser]);

    const getOtherParticipantId = (conv: Conversation) => {
        return conv.participants.find(p => p !== currentUser?.uid) || 'unknown';
    };

    const resolveUser = (uid: string) => {
        const u = users.find(user => user.uid === uid);
        return {
            name: u?.displayName || 'Unknown User',
            photo: u?.photoURL,
        };
    };

    const handleConversationClick = (convId: string) => {
        navigate('/messages', { state: { conversationId: convId } });
    };

    if (!currentUser) return null;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full bg-gray-50 hover:bg-gray-100 text-gray-600 relative"
                    aria-label="Messages"
                >
                    <MessageCircle className="h-5 w-5" />
                    {totalUnread > 0 && (
                        <span className="absolute top-0 end-0 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-medium text-white shadow-sm border-2 border-white">
                            {totalUnread > 9 ? '9+' : totalUnread}
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 shadow-xl border-border/60">
                <div className="flex items-center justify-between px-4 py-3 bg-muted/30">
                    <DropdownMenuLabel className="font-bold text-base p-0">Messages</DropdownMenuLabel>
                    {totalUnread > 0 && (
                        <span className="text-xs text-muted-foreground">
                            {totalUnread} unread
                        </span>
                    )}
                </div>
                <DropdownMenuSeparator className="m-0" />
                <ScrollArea className="h-[280px]">
                    {recentConversations.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground p-4 text-center">
                            <div className="bg-muted p-4 rounded-full mb-3">
                                <MessageCircle className="h-6 w-6 opacity-30" />
                            </div>
                            <h4 className="font-semibold text-sm mb-1">No messages</h4>
                            <p className="text-xs">Start a conversation from someone's profile.</p>
                        </div>
                    ) : (
                        <div>
                            {recentConversations.map(conv => {
                                const otherId = getOtherParticipantId(conv);
                                const user = resolveUser(otherId);
                                const isUnread = (conv.unreadCounts[currentUser?.uid || ''] || 0) > 0;

                                return (
                                    <DropdownMenuItem
                                        key={conv.id}
                                        className="flex items-start gap-3 p-3 cursor-pointer focus:bg-accent"
                                        onClick={() => handleConversationClick(conv.id)}
                                    >
                                        <Avatar className="h-10 w-10 mt-0.5">
                                            <AvatarImage src={user.photo || ''} />
                                            <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className={`text-sm truncate ${isUnread ? 'font-semibold' : ''}`}>
                                                    {user.name}
                                                </span>
                                                {conv.lastMessageAt && (
                                                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                                        {formatDistanceToNow(conv.lastMessageAt, { addSuffix: false, locale: getDateFnsLocale() })}
                                                    </span>
                                                )}
                                            </div>
                                            <p className={`text-xs line-clamp-1 mt-0.5 ${isUnread ? 'text-foreground' : 'text-muted-foreground'}`}>
                                                {conv.lastSenderId === currentUser?.uid ? 'You: ' : ''}
                                                {conv.lastMessage || 'Sent an attachment'}
                                            </p>
                                            {isUnread && (
                                                <div className="flex justify-end mt-1">
                                                    <span className="h-2 w-2 rounded-full bg-primary" />
                                                </div>
                                            )}
                                        </div>
                                    </DropdownMenuItem>
                                );
                            })}
                        </div>
                    )}
                </ScrollArea>
                <DropdownMenuSeparator className="m-0" />
                <div className="p-2 text-center bg-muted/30">
                    <Button variant="ghost" size="sm" className="w-full text-xs text-primary" asChild>
                        <Link to="/messages">View all messages</Link>
                    </Button>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
